import { Link } from "react-router-dom";
import { Mail, Quote, ArrowRight, ShieldCheck, MapPin, Fingerprint } from "lucide-react";
import { PageIntro, PageBadgeBand, Section } from "@/components/site/PageShell";
import StoreBadges from "@/components/site/StoreBadges";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";

/* , STORIES , */
/**
 * Template shape. When a real user outcome arrives, replace one of these
 * entries entirely, same shape, no other page changes needed.
 *
 *   {
 *     id,               // route-safe key
 *     kind,             // 'illustrative' | 'real'
 *     beachhead,        // 'accused' | 'missing' | 'altercations'
 *     beachheadLabel,
 *     title,            // headline for the story
 *     setup,            // one-line context
 *     narrative[],      // 2–3 short paragraphs
 *     productBeat,      // one-line closing punch that names the sealed record
 *     linkTo,           // /accused | /missing | /altercations
 *     beatIcon,         // lucide icon for the product beat row
 *   }
 */
const STORIES = [
  {
    id: "accused-hr",
    kind: "illustrative",
    beachhead: "accused",
    beachheadLabel: "Falsely accused",
    title: "The accusation that fell apart against a sealed record.",
    setup: "A workplace complaint · Illustrative scenario.",
    narrative: [
      "It began as a five-minute conversation in a stairwell. A team lead and a colleague, disagreeing about a project handoff. The team lead had started recording the moment the conversation began, as they do with any exchange they think might come back around later.",
      "Two days later, the complaint arrived at HR: the team lead had raised their voice, made a threat, refused to listen. The retelling was clean, calm, and specific. Human resources opened a file.",
      "The team lead signed a release authorizing YeboNadi to decrypt and deliver the sealed audio to the HR partner. Their encryption/decryption key went with the release. YeboNadi delivered the record via secure cloud storage. HR listened once. The complaint was withdrawn the next morning.",
    ],
    productBeat:
      "Not a shouting match about who remembers what. A sealed record HR could actually hear.",
    linkTo: "/accused",
    beatIcon: Fingerprint,
  },
  {
    id: "missing-timeline",
    kind: "illustrative",
    beachhead: "missing",
    beachheadLabel: "If you go missing",
    title: "The family that almost didn't have a timeline.",
    setup: "A late rideshare · Illustrative scenario.",
    narrative: [
      "She had installed YeboNadi months earlier, set it up once, and then never mentioned it. There was no reason to. Nothing bad ever happened. The trip that night was routine, right up until it wasn't.",
      "Her partner did not know she was using YeboNadi that night. She had installed the app months earlier and never mentioned it. When she was two hours late, he did the only thing he could: he called it in. It was only later, when investigators pulled her cloud accounts, that the sealed recording surfaced. It took a legal order and a great deal of time. She was okay. The story could easily have gone the other way. This is why you tell one person you have it, and share your key.",
      "The app was doing exactly what it was built to do. The people who loved her simply did not know to ask.",
    ],
    productBeat:
      "Tell one person. Share your key.",
    linkTo: "/missing",
    beatIcon: MapPin,
  },
  {
    id: "altercations-parking",
    kind: "illustrative",
    beachhead: "altercations",
    beachheadLabel: "Altercations",
    title: "The confrontation where the sealed version ended the argument.",
    setup: "A parking-lot dispute · Illustrative scenario.",
    narrative: [
      "It was a fender bender in a supermarket parking lot. Two cars, one dented panel, no injuries. The other driver got out of his car and started narrating, loud, fast, and confident, that she had reversed into him without looking.",
      "She tapped record. One tap, no unlock ceremony, no framing shot. The conversation continued for eleven minutes: her explanation, his rising voice, a bystander's contribution, the arrival of a police officer. Every second of it was encrypted and hash-sealed on her phone as it was captured.",
      "She signed a release authorizing YeboNadi to decrypt and deliver the sealed recording to her insurer, along with her encryption/decryption key. YeboNadi delivered the record via secure cloud storage. Between the audio, the timestamp, and the GPS confirming both cars were stationary when the exchange began, the claim was reversed within days.",
    ],
    productBeat:
      "The other side's version arrived first. Hers arrived sealed.",
    linkTo: "/altercations",
    beatIcon: ShieldCheck,
  },
];

function StoryCard({ story }) {
  return (
    <article
      className="rounded-2xl border border-[color:var(--yn-border)] bg-white p-6 sm:p-8 lg:p-10"
      data-testid={SITE.storiesCard}
      data-story-id={story.id}
      data-story-kind={story.kind}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          {story.beachheadLabel}
        </p>
        {story.kind === "illustrative" && (
          <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
            Illustrative scenario
          </span>
        )}
      </div>

      <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-bold text-[color:var(--yn-ink)] tracking-tight leading-snug max-w-3xl">
        {story.title}
      </h2>
      <p className="mt-2 text-sm text-[color:var(--yn-muted)]">
        {story.setup}
      </p>

      <div className="mt-6 space-y-4 text-[16px] leading-[1.75] text-[color:var(--yn-ink)]/85 max-w-3xl">
        {story.narrative.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Product beat row */}
      <div className="mt-8 rounded-xl bg-[color:var(--yn-subtle)] border border-[color:var(--yn-border)] p-5 flex items-start gap-4 max-w-3xl">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          style={{ background: "rgba(26,108,196,0.1)" }}
        >
          <story.beatIcon size={20} style={{ color: "var(--yn-primary)" }} />
        </span>
        <p className="font-heading text-[17px] sm:text-lg font-semibold text-[color:var(--yn-ink)] leading-snug">
          {story.productBeat}
        </p>
      </div>

      {/* Store badges + read-more link */}
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <StoreBadges
          placement={`story-${story.id}`}
          appTestId={`story-${story.id}-app-store-badge`}
          playTestId={`story-${story.id}-play-store-badge`}
        />
        <Link
          to={story.linkTo}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--yn-primary)] hover:text-[color:var(--yn-primary-hover)]"
        >
          Read more on {story.beachheadLabel.toLowerCase()}
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}

const SHARE_MAILTO = `mailto:info@concepts-simplified.com?subject=${encodeURIComponent(
  "YeboNadi, my story",
)}&body=${encodeURIComponent(
  "Tell us what happened. Be as brief or as detailed as you like, a human on our team will read it. Nothing is ever published without your written permission.\n\n(Feel free to paste the story here.)\n\n",
)}`;

function ShareYourStory() {
  return (
    <div
      className="rounded-2xl border border-[color:var(--yn-border)] bg-white p-6 sm:p-10 max-w-3xl"
      data-testid={SITE.storiesShare}
    >
      <div className="flex items-center gap-2">
        <Quote size={22} style={{ color: "var(--yn-primary)" }} />
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
          Share your story
        </p>
      </div>
      <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[color:var(--yn-ink)] tracking-tight">
        Real outcomes replace the scenarios above.
      </h2>
      <p className="mt-4 text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
        When a sealed record made a difference for you, or someone you love ,
        we would like to hear about it. Nothing is published without your
        written permission. A human on our team will read every email.
      </p>
      <a
        href={SHARE_MAILTO}
        className="mt-6 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors"
      >
        <Mail size={16} />
        Email your story
      </a>
      <p className="mt-4 text-sm text-[color:var(--yn-muted)]">
        Or write directly to{" "}
        <a
          href="mailto:info@concepts-simplified.com"
          className="text-[color:var(--yn-primary)] hover:underline"
        >
          info@concepts-simplified.com
        </a>
        .
      </p>
    </div>
  );
}

export default function Stories() {
  useMeta({
    title: "Stories, how sealed records change what happens next, YeboNadi",
    description:
      "Three illustrative scenarios of what a sealed YeboNadi record can change, and what happens when you don't share the key: an accusation that fell apart, a family that almost didn't have a timeline, a confrontation ended by the sealed version. Real outcomes replace these when they arrive.",
    path: "/stories",
  });

  return (
    <div data-testid={SITE.storiesPage}>
      <PageIntro
        kicker="Stories"
        title="Real moments. Real records."
        lede="We're a new product built for a hard problem. Rather than invent testimonials, we've written three illustrative scenarios that show what a sealed record can change. When real user outcomes arrive, they replace these, one at a time, only with permission."
      />

      {/* Illustrative-scenarios label banner */}
      <Section className="!pt-0">
        <div className="max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
          <p className="text-sm leading-relaxed text-amber-900">
            <strong>Illustrative scenarios, not testimonials.</strong> The
            three stories below are composites written to show how the product
            works in the three situations YeboNadi is built for. No individual
            user is depicted. When we have real, permissioned outcomes to
            share, we will replace these one at a time and mark them clearly.
          </p>
        </div>
      </Section>

      {/* Story cards */}
      <Section className="!pt-0">
        <div className="space-y-6 lg:space-y-8">
          {STORIES.map((s) => (
            <StoryCard key={s.id} story={s} />
          ))}
        </div>
      </Section>

      <PageBadgeBand heading="Have the app before you have the story." />

      {/* Share your story */}
      <Section>
        <ShareYourStory />
      </Section>
    </div>
  );
}
